import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { KeycloakService } from 'keycloak-angular';
import { TokenStorageService } from './token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class KeycloakSessionService {

  private refreshInFlight?: Promise<string | null>;
  private readonly isBrowser: boolean;

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly tokenStorage: TokenStorageService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  async syncTokensFromKeycloak(): Promise<string | null> {
    if (!this.isBrowser) {
      return null;
    }

    try {
      const token = await this.keycloakService.getToken();
      const refreshToken = this.keycloakService.getKeycloakInstance()?.refreshToken;
      console.debug('[KeycloakSession] Sync tokens from adapter:', {
        hasAccessToken: !!token,
        hasRefreshToken: !!refreshToken
      });
      this.persistTokens(token, refreshToken);
      return token ?? null;
    } catch {
      console.debug('[KeycloakSession] Unable to sync tokens from adapter');
      return null;
    }
  }

  hydrateKeycloakSession(token: string, refreshToken: string): void {
    if (!this.isBrowser) {
      return;
    }

    const keycloakAuth = this.keycloakService.getKeycloakInstance();

    keycloakAuth.token = token;
    keycloakAuth.refreshToken = refreshToken;
    keycloakAuth.authenticated = true;
    console.debug('[KeycloakSession] Hydrating keycloak session with access and refresh token');

    try {
      const tokenParsed = this.tokenStorage.getTokenPayload(token);
      const refreshTokenParsed = this.tokenStorage.getTokenPayload(refreshToken);

      keycloakAuth.tokenParsed = tokenParsed;
      keycloakAuth.refreshTokenParsed = refreshTokenParsed;

      if (tokenParsed?.session_state) keycloakAuth.sessionId = tokenParsed.session_state;
      if (tokenParsed?.sub) keycloakAuth.subject = tokenParsed.sub;
      if (tokenParsed?.realm_access) keycloakAuth.realmAccess = tokenParsed.realm_access;
      if (tokenParsed?.resource_access) keycloakAuth.resourceAccess = tokenParsed.resource_access;
      if (typeof tokenParsed?.iat === 'number') {
        keycloakAuth.timeSkew = Math.floor(Date.now() / 1000) - tokenParsed.iat;
      }
    } catch (error) {
      console.debug('[KeycloakSession] Error parsing token:', error);
    }

    this.persistTokens(token, refreshToken);
  }

  bindTokenExpiryHandler(onSessionExpired: (reason: string) => void, reason = 'Access token expired'): void {
    if (!this.isBrowser) {
      return;
    }

    const keycloakAuth = this.keycloakService.getKeycloakInstance();

    if (!keycloakAuth) {
      return;
    }

    keycloakAuth.onTokenExpired = () => {
      console.debug('[KeycloakSession] onTokenExpired fired');
      void this.getValidToken(0, reason).then(token => {
        if (!token) {
          onSessionExpired(reason);
        }
      });
    };
  }

  async getValidToken(minValidity: number, reason: string): Promise<string | null> {
    if (!this.isBrowser) {
      return null;
    }

    if (this.refreshInFlight) {
      console.debug('[KeycloakSession] Reusing in-flight token refresh:', { reason, minValidity });
      return this.refreshInFlight;
    }

    const tokenPromise = (async () => {
      const keycloakToken = await this.tryRefreshAndReadKeycloakToken(minValidity, reason);
      if (keycloakToken) {
        console.debug('[KeycloakSession] Using token from keycloak session:', { reason, minValidity });
        return keycloakToken;
      }

      const storedToken = this.getStoredValidToken();
      console.debug('[KeycloakSession] Falling back to stored token:', {
        reason,
        minValidity,
        hasStoredToken: !!storedToken
      });
      return storedToken;
    })().finally(() => {
      this.refreshInFlight = undefined;
    });

    this.refreshInFlight = tokenPromise;
    return tokenPromise;
  }

  clearSession(): void {
    console.debug('[KeycloakSession] Clearing keycloak memory and local auth storage');
    try {
      this.keycloakService.clearToken();
    } catch {
      console.debug('[KeycloakSession] Unable to clear keycloak token from adapter');
    }
    this.tokenStorage.signOut();
  }

  private async tryRefreshAndReadKeycloakToken(minValidity: number, reason: string): Promise<string | null> {
    const isLoggedIn = await Promise.resolve(this.keycloakService.isLoggedIn() as boolean | Promise<boolean>);

    if (!isLoggedIn) {
      console.debug('[KeycloakSession] Keycloak is not logged in, skipping refresh:', { reason });
      return null;
    }

    const currentKeycloakToken = await this.syncTokensFromKeycloak();
    if (currentKeycloakToken && !this.isTokenExpiringWithin(currentKeycloakToken, minValidity)) {
      console.debug('[KeycloakSession] Using current access token from keycloak adapter without refresh:', {
        reason,
        minValidity
      });
      return currentKeycloakToken;
    }

    const keycloakRefreshToken = this.keycloakService.getKeycloakInstance()?.refreshToken;

    if (keycloakRefreshToken) {
      try {
        console.debug('[KeycloakSession] Using refresh token to update access token:', {
          reason,
          minValidity
        });
        await this.keycloakService.updateToken(minValidity);
        console.debug('[KeycloakSession] Refresh token flow completed:', {
          reason,
          minValidity
        });
      } catch (error) {
        console.debug('[KeycloakSession] Token refresh failed:', reason, error);
      }
    } else {
      console.debug('[KeycloakSession] No refresh token available, cannot refresh access token:', {
        reason,
        minValidity
      });
    }

    const keycloakToken = await this.syncTokensFromKeycloak();
    return keycloakToken && !this.tokenStorage.isTokenExpired(keycloakToken)
      ? keycloakToken
      : null;
  }

  private getStoredValidToken(): string | null {
    const storedToken = this.tokenStorage.getToken();
    return storedToken && !this.tokenStorage.isTokenExpired(storedToken)
      ? storedToken
      : null;
  }

  private isTokenExpiringWithin(token: string, minValidity: number): boolean {
    const tokenPayload = this.tokenStorage.getTokenPayload(token);
    const expirationTime = typeof tokenPayload?.exp === 'number' ? tokenPayload.exp : null;

    if (!expirationTime) {
      return true;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return expirationTime <= nowInSeconds + Math.max(minValidity, 0);
  }

  private persistTokens(token?: string | null, refreshToken?: string | null): void {
    if (token) {
      this.tokenStorage.saveToken(token);
    }

    if (refreshToken) {
      this.tokenStorage.saveRefreshToken(refreshToken);
    }
  }
}
